import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    App,
    Button,
    Card,
    Checkbox,
    Collapse,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Progress,
    Select,
    Space,
    Spin,
    Table,
    Tabs,
    Tag,
    Tooltip
} from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    ImportOutlined,
    PauseCircleOutlined,
    PlusOutlined,
    ReloadOutlined,
    StopOutlined
} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import dayjs from 'dayjs';
import {aiBacktestService, AiBacktestTask, FreqtradeDataFile, KlineCoverage} from '../../services/aiBacktestService';
import {useAuth} from '../../contexts/AuthContext';

const STATUS_CONFIG: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
    RUNNING: {color: 'processing', icon: <ClockCircleOutlined/>, label: '运行中'},
    COMPLETED: {color: 'success', icon: <CheckCircleOutlined/>, label: '已完成'},
    FAILED: {color: 'error', icon: <ExclamationCircleOutlined/>, label: '失败'},
    STOPPED: {color: 'warning', icon: <StopOutlined/>, label: '已停止'},
    PENDING: {color: 'default', icon: <ClockCircleOutlined/>, label: '等待中'},
    FETCHING_DATA: {color: 'processing', icon: <ClockCircleOutlined/>, label: '获取数据中'},
};

const DECISION_FREQS = [
    {value: '5m', label: '5分钟'},
    {value: '10m', label: '10分钟'},
    {value: '15m', label: '15分钟'},
    {value: '20m', label: '20分钟'},
    {value: '30m', label: '30分钟'},
];
const INST_IDS = ['BTC-USDT-SWAP', 'ETH-USDT-SWAP', 'SOL-USDT-SWAP', 'DOGE-USDT-SWAP', 'XRP-USDT-SWAP'];

const AiBacktestListPage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const navigate = useNavigate();
    const {user} = useAuth();
    const userId = user?.id || 1;

    const [tasks, setTasks] = useState<AiBacktestTask[]>([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('RUNNING');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalStep, setModalStep] = useState<'form' | 'overview'>('form');
    const [estimateData, setEstimateData] = useState<any>(null);
    const [estimateLoading, setEstimateLoading] = useState(false);
    const formValuesRef = useRef<any>(null);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [coverage, setCoverage] = useState<KlineCoverage[]>([]);
    const [importFiles, setImportFiles] = useState<FreqtradeDataFile[]>([]);
    const [importing, setImporting] = useState(false);
    const [coverageExpanded, setCoverageExpanded] = useState(false);
    const [availableModels, setAvailableModels] = useState<{ value: string; label: string; provider: string }[]>([]);
    const [form] = Form.useForm();
    const [filterInstIds, setFilterInstIds] = useState<string[]>([]);
    const [filterTimeframes, setFilterTimeframes] = useState<string[]>([]);
    const [filterDateRange, setFilterDateRange] = useState<any>(null);

    const fetchTasks = useCallback(async () => {
        setLoading(true);
        try {
            const res = await aiBacktestService.getTasks(userId, activeTab === 'HISTORY' ? undefined : activeTab);
            let data = res.data?.data;
            if (!data) data = [];
            if (activeTab === 'HISTORY') {
                data = data.filter((t: AiBacktestTask) => t.status !== 'RUNNING' && t.status !== 'PENDING' && t.status !== 'FETCHING_DATA');
            }
            setTasks(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('获取任务列表失败', e);
        } finally {
            setLoading(false);
        }
    }, [userId, activeTab]);

    const fetchCoverage = useCallback(async () => {
        try {
            const res = await aiBacktestService.getKlineCoverage();
            setCoverage(Array.isArray(res.data?.data) ? res.data.data : []);
        } catch (e) {
            console.error('获取数据覆盖失败', e);
        }
    }, []);

    useEffect(() => {
        fetchTasks();
        fetchCoverage();
    }, [fetchTasks, fetchCoverage]);

    useEffect(() => {
        const timer = setInterval(fetchTasks, 10000);
        return () => clearInterval(timer);
    }, [fetchTasks]);

    const doCreateBacktest = async (values: any) => {
        const timeRange = values.timeRange;
        return aiBacktestService.startBacktest({
            taskName: values.taskName || `${values.instId} ${values.decisionFrequency || '5m'} 回测`,
            userId,
            instId: values.instId,
            exchange: values.exchange || 'OKX',
            decisionFrequency: values.decisionFrequency || '5m',
            startTime: timeRange[0].valueOf(),
            endTime: timeRange[1].valueOf(),
            initialCapital: values.initialCapital || 10000,
            modelName: values.modelName || 'deepseek-v4-pro',
            maxPositions: values.maxPositions,
            maxAmountPerTrade: values.maxAmountPerTrade,
            leverage: values.leverage,
        });
    };

    const handleRerun = async (taskId: number) => {
        try {
            const res = await aiBacktestService.rerunBacktest(taskId);
            if (res.data?.data) {
                messageApi.success('重跑任务已创建');
                fetchTasks();
            }
        } catch (e: any) {
            messageApi.error('重跑失败: ' + (e?.response?.data?.message || e.message));
        }
    };

    const handleStop = async (taskId: number) => {
        try {
            await aiBacktestService.stopBacktest(taskId);
            messageApi.success('已停止');
            fetchTasks();
        } catch (e: any) {
            messageApi.error('停止失败');
        }
    };

    const handleDelete = async (taskId: number) => {
        try {
            await aiBacktestService.deleteTask(taskId);
            messageApi.success('已删除');
            fetchTasks();
        } catch (e: any) {
            messageApi.error('删除失败');
        }
    };

    const handleImport = async () => {
        setImporting(true);
        try {
            const res = await aiBacktestService.importFreqtradeData('/tmp/main-docker', 'paper');
            const data = res.data?.data;
            messageApi.success(`导入完成: ${data?.totalSuccess || 0} 条`);
            setImportModalOpen(false);
            fetchCoverage();
        } catch (e: any) {
            messageApi.error('导入失败: ' + (e?.response?.data?.message || e.message));
        } finally {
            setImporting(false);
        }
    };

    const fetchAvailableModels = useCallback(async () => {
        try {
            const res = await aiBacktestService.getAvailableModels();
            const data = res.data?.data;
            if (Array.isArray(data)) {
                setAvailableModels(data.map((m: any) => ({
                    value: m.modelId,
                    label: `${m.displayName} (${m.provider})`,
                    provider: m.provider,
                })));
            }
        } catch (e) {
            console.error('获取可用模型失败', e);
        }
    }, []);

    const openNewModal = () => {
        fetchAvailableModels();
        setModalStep('form');
        setEstimateData(null);
        formValuesRef.current = null;
        setModalOpen(true);
        setTimeout(() => {
            form.setFieldsValue({
                timeRange: [dayjs().subtract(1, 'day').startOf('day'),
                    dayjs().subtract(1, 'day').startOf('day').add(12, 'hour')],
                exchange: 'OKX',
                initialCapital: 10000,
                decisionFrequency: '5m',
            });
        }, 0);
    };

    const handleNextStep = async () => {
        try {
            const values = await form.validateFields();
            formValuesRef.current = values;
            setEstimateLoading(true);
            const timeRange = values.timeRange;
            const res = await aiBacktestService.estimate({
                instId: values.instId,
                decisionFrequency: values.decisionFrequency || '5m',
                startTime: timeRange[0].valueOf(),
                endTime: timeRange[1].valueOf(),
            });
            const data = res.data?.data;
            if (data) {
                if (!data.valid) {
                    messageApi.warning(data.message || '数据不足，无法启动回测');
                    return;
                }
                setEstimateData(data);
                setModalStep('overview');
            }
        } catch (e: any) {
            if (e?.errorFields) return; // 表单验证错误，不处理
            messageApi.error('评估失败: ' + (e?.response?.data?.message || e.message));
        } finally {
            setEstimateLoading(false);
        }
    };

    const handleConfirmStart = async () => {
        const values = formValuesRef.current;
        if (!values) {
            messageApi.error('表单数据丢失，请重新填写');
            setModalStep('form');
            return;
        }
        setEstimateLoading(true);
        try {
            const res = await doCreateBacktest(values);
            if (res.data?.data) {
                messageApi.success('回测任务已创建');
                setModalOpen(false);
                setModalStep('form');
                setEstimateData(null);
                form.resetFields();
                fetchTasks();
            } else {
                messageApi.error('创建失败：服务器返回异常');
            }
        } catch (e: any) {
            // 拦截器 reject 的是 new Error(message), 优先取 e.message
            const errMsg = e?.response?.data?.message || e.message || '创建失败';
            messageApi.error('启动失败: ' + errMsg);
        } finally {
            setEstimateLoading(false);
        }
    };

    const openImportModal = async () => {
        setImportModalOpen(true);
        try {
            const res = await aiBacktestService.listFreqtradeFiles();
            setImportFiles(Array.isArray(res.data?.data) ? res.data.data : []);
        } catch (e) {
            console.error('获取Freqtrade文件列表失败', e);
        }
    };

    const onInstIdChange = (instId: string) => {
        if (instId) {
            const c = coverage.find(c => c.instId === instId);
            if (c) {
                form.setFieldsValue({
                    timeRange: [dayjs(c.minTime), dayjs(c.maxTime)]
                });
            }
        }
    };

    const renderTaskCard = (task: AiBacktestTask) => {
        const cfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.PENDING;
        return (
            <Card
                key={task.id}
                size="small"
                style={{marginBottom: 12, background: 'var(--pt-bg-panel)', borderColor: 'var(--pt-border-subtle)'}}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 8
                }}>
                    <div style={{flex: 1}}>
                        <Space>
                            <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>
                            <strong style={{color: 'var(--pt-text-primary)'}}>{task.taskName}</strong>
                        </Space>
                        <div style={{color: 'var(--pt-text-secondary)', fontSize: 13, marginTop: 6}}>
                            {task.instId} · {task.decisionFrequency} 决策
                            {' · '}{formatTs(task.startTime)} ~ {formatTs(task.endTime)}
                        </div>
                        <div style={{color: 'var(--pt-text-dim)', fontSize: 12, marginTop: 2}}>
                            初始 {task.initialCapital} USDT · {task.modelName}
                        </div>
                    </div>
                    <Space>
                        {(task.status === 'RUNNING' || task.status === 'FETCHING_DATA') && (
                            <>
                                <Tooltip title="暂停"><Button size="small" icon={<PauseCircleOutlined/>}
                                                              onClick={() => handleStop(task.id)}/></Tooltip>
                                <Tooltip title="停止"><Button size="small" icon={<StopOutlined/>}
                                                              onClick={() => handleStop(task.id)}/></Tooltip>
                                <Tooltip title="查看详情"><Button size="small" type="primary" icon={<EyeOutlined/>}
                                                                  onClick={() => navigate(`/ai-backtest/${task.id}`)}/></Tooltip>
                            </>
                        )}
                        {task.status === 'COMPLETED' && (
                            <>
                                <Tooltip title="查看详情"><Button size="small" type="primary" icon={<EyeOutlined/>}
                                                                  onClick={() => navigate(`/ai-backtest/${task.id}`)}/></Tooltip>
                                <Tooltip title="重跑"><Button size="small" icon={<ReloadOutlined/>}
                                                              onClick={() => handleRerun(task.id)}/></Tooltip>
                                <Tooltip title="删除">
                                    <Popconfirm title="确认删除?" onConfirm={() => handleDelete(task.id)}>
                                        <Button size="small" danger icon={<DeleteOutlined/>}/>
                                    </Popconfirm>
                                </Tooltip>
                            </>
                        )}
                        {(task.status === 'FAILED' || task.status === 'STOPPED') && (
                            <>
                                <Tooltip title="查看详情"><Button size="small" type="primary" icon={<EyeOutlined/>}
                                                                  onClick={() => navigate(`/ai-backtest/${task.id}`)}/></Tooltip>
                                <Tooltip title="重跑"><Button size="small" icon={<ReloadOutlined/>}
                                                              onClick={() => handleRerun(task.id)}/></Tooltip>
                                <Tooltip title="删除">
                                    <Popconfirm title="确认删除?" onConfirm={() => handleDelete(task.id)}>
                                        <Button size="small" danger icon={<DeleteOutlined/>}/>
                                    </Popconfirm>
                                </Tooltip>
                            </>
                        )}
                    </Space>
                </div>
                {(task.status === 'RUNNING' || task.status === 'FETCHING_DATA') && (
                    <>
                        <Progress percent={Math.round(task.progress || 0)} size="small" style={{marginTop: 8}}/>
                        {task.progressMessage && (
                            <div style={{color: 'var(--pt-text-dim)', fontSize: 12, marginTop: 4}}>
                                {task.progressMessage}
                                {task.progressKlineCount != null && task.progressKlineCount > 0 && (
                                    <span>（已拉取 {task.progressKlineCount} 根K线）</span>
                                )}
                            </div>
                        )}
                    </>
                )}
            </Card>
        );
    };

    const coverageColumns = [
        {title: '交易对', dataIndex: 'instId', key: 'instId'},
        {title: '周期', dataIndex: 'timeframe', key: 'timeframe', width: 60},
        {
            title: '数据范围', key: 'range', render: (_: any, r: KlineCoverage) =>
                `${formatTs(r.minTime)} ~ ${formatTs(r.maxTime)}`
        },
        {title: 'K线数', dataIndex: 'totalBars', key: 'totalBars', width: 80},
    ];

    const filteredCoverage = coverage.filter(r => {
        if (filterInstIds.length > 0 && !filterInstIds.includes(r.instId)) return false;
        if (filterTimeframes.length > 0 && !filterTimeframes.includes(r.timeframe)) return false;
        if (filterDateRange && filterDateRange[0] && filterDateRange[1]) {
            const start = filterDateRange[0].valueOf();
            const end = filterDateRange[1].valueOf();
            if (r.maxTime < start || r.minTime > end) return false;
        }
        return true;
    });

    const uniqueInstIds = [...new Set(coverage.map(r => r.instId))].sort();
    const uniqueTimeframes = [...new Set(coverage.map(r => r.timeframe))].sort();

    return (
        <div style={{padding: 20, maxWidth: 1200, margin: '0 auto'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
                <h2 style={{color: 'var(--pt-text-primary)', margin: 0}}>AI交易回测</h2>
                <Space>
                    <Tooltip title="从Freqtrade导入历史数据">
                        <Button icon={<ImportOutlined/>} onClick={openImportModal}/>
                    </Tooltip>
                    <Tooltip title="新建AI回测">
                        <Button type="primary" icon={<PlusOutlined/>} onClick={openNewModal}/>
                    </Tooltip>
                </Space>
            </div>

            <Collapse
                activeKey={coverageExpanded ? ['coverage'] : []}
                onChange={(keys) => setCoverageExpanded(keys.includes('coverage'))}
                style={{marginBottom: 16, background: 'var(--pt-bg-panel)', borderColor: 'var(--pt-border-subtle)'}}
                items={[{
                    key: 'coverage',
                    label: `数据概览（${filteredCoverage.length} / ${coverage.length} 条覆盖记录）`,
                    children: (
                        <div>
                            <Space wrap style={{marginBottom: 12}}>
                                <Select
                                    mode="multiple"
                                    placeholder="交易对"
                                    style={{minWidth: 160}}
                                    allowClear
                                    value={filterInstIds}
                                    onChange={setFilterInstIds}
                                    options={uniqueInstIds.map(i => ({value: i, label: i}))}
                                    maxTagCount={2}
                                />
                                <Select
                                    mode="multiple"
                                    placeholder="周期"
                                    style={{minWidth: 120}}
                                    allowClear
                                    value={filterTimeframes}
                                    onChange={setFilterTimeframes}
                                    options={uniqueTimeframes.map(t => ({value: t, label: t}))}
                                    maxTagCount={2}
                                />
                                <DatePicker.RangePicker
                                    showTime
                                    value={filterDateRange}
                                    onChange={setFilterDateRange}
                                    style={{minWidth: 340}}
                                    allowClear
                                />
                                {(filterInstIds.length > 0 || filterTimeframes.length > 0 || filterDateRange) && (
                                    <Button size="small" onClick={() => {
                                        setFilterInstIds([]);
                                        setFilterTimeframes([]);
                                        setFilterDateRange(null);
                                    }}>清除筛选</Button>
                                )}
                            </Space>
                            <Table
                                dataSource={filteredCoverage}
                                columns={coverageColumns}
                                rowKey={(r) => `${r.instId}-${r.timeframe}`}
                                size="small"
                                pagination={false}
                                scroll={{x: 500}}
                            />
                        </div>
                    )
                }]}
            />

            <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
                {key: 'RUNNING', label: '运行中', children: null},
                {key: 'STOPPED', label: '已暂停', children: null},
                {key: 'HISTORY', label: '历史记录', children: null},
            ]}/>

            <Spin spinning={loading}>
                <div style={{minHeight: 200}}>
                    {tasks.length === 0 ? (
                        <div style={{textAlign: 'center', color: 'var(--pt-text-dim)', padding: 60}}>
                            暂无任务，点击"新建回测"开始
                        </div>
                    ) : (
                        tasks.map(renderTaskCard)
                    )}
                </div>
            </Spin>

            <Modal
                title={modalStep === 'form' ? '新建AI回测' : '任务概览'}
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false);
                    setModalStep('form');
                    setEstimateData(null);
                }}
                footer={modalStep === 'form' ? [
                    <Button key="cancel" onClick={() => {
                        setModalOpen(false);
                        setModalStep('form');
                        setEstimateData(null);
                    }}>取消</Button>,
                    <Button key="next" type="primary" loading={estimateLoading}
                            onClick={handleNextStep}>下一步</Button>,
                ] : [
                    <Button key="back" onClick={() => setModalStep('form')}>返回修改</Button>,
                    <Button key="confirm" type="primary" loading={estimateLoading}
                            onClick={handleConfirmStart}>确认启动</Button>,
                ]}
                width={520}
                className="dark-modal-confirm"
            >
                {modalStep === 'form' ? (
                    <Form form={form} layout="vertical" initialValues={{
                        initialCapital: 10000,
                        decisionFrequency: '5m',
                        timeRange: [dayjs().subtract(1, 'day').startOf('day'),
                            dayjs().subtract(1, 'day').startOf('day').add(12, 'hour')],
                    }}>
                        <Form.Item name="taskName" label="任务名称">
                            <Input placeholder="可选，自动生成"/>
                        </Form.Item>
                        <Form.Item name="instId" label="交易对" rules={[{required: true, message: '请选择交易对'}]}>
                            <Select options={INST_IDS.map(i => ({value: i, label: i}))} onChange={onInstIdChange}/>
                        </Form.Item>
                        <Form.Item name="exchange" label="交易所" initialValue="OKX">
                            <Select options={[{value: 'OKX', label: 'OKX'}, {value: 'Bybit', label: 'Bybit'}]}/>
                        </Form.Item>
                        <Form.Item name="timeRange" label="时间范围"
                                   rules={[{required: true, message: '请选择时间范围'}]}>
                            <DatePicker.RangePicker showTime={{format: 'HH:mm'}} format="YYYY-MM-DD HH:mm"
                                                    style={{width: '100%'}}/>
                        </Form.Item>
                        <Form.Item name="decisionFrequency" label="决策频次">
                            <Select options={DECISION_FREQS}/>
                        </Form.Item>
                        <Form.Item name="initialCapital" label="初始资金 (USDT)">
                            <InputNumber min={100} max={1000000} style={{width: '100%'}}/>
                        </Form.Item>
                        <Form.Item name="modelName" label="AI模型（交易/量化/通用模块）">
                            <Select
                                placeholder="选择模型"
                                loading={availableModels.length === 0}
                                options={availableModels}
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                        <Collapse ghost items={[{
                            key: 'advanced',
                            label: '高级选项',
                            children: (
                                <>
                                    <Form.Item name="maxPositions" label="最大持仓数">
                                        <InputNumber min={1} max={10} style={{width: '100%'}}/>
                                    </Form.Item>
                                    <Form.Item name="maxAmountPerTrade" label="单次最大金额 (USDT)">
                                        <InputNumber min={10} max={100000} style={{width: '100%'}}/>
                                    </Form.Item>
                                    <Form.Item name="leverage" label="杠杆倍数">
                                        <InputNumber min={1} max={100} style={{width: '100%'}}/>
                                    </Form.Item>
                                </>
                            )
                        }]}/>
                    </Form>
                ) : (
                    <div style={{color: 'var(--pt-text-primary)'}}>
                        {estimateData && (
                            <>
                                <Card size="small" style={{
                                    marginBottom: 12,
                                    background: 'var(--pt-bg-card)',
                                    borderColor: 'var(--pt-border-subtle)'
                                }}>
                                    <div style={{color: 'var(--pt-text-dim)', fontSize: 12, marginBottom: 8}}>回测参数
                                    </div>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '8px 24px',
                                        fontSize: 14
                                    }}>
                                        <div>
                                            <span style={{color: 'var(--pt-text-dim)'}}>交易对: </span>
                                            <span>{estimateData.instId}</span>
                                        </div>
                                        <div>
                                            <span style={{color: 'var(--pt-text-dim)'}}>决策频次: </span>
                                            <span>{estimateData.decisionFrequency}</span>
                                        </div>
                                        <div>
                                            <span style={{color: 'var(--pt-text-dim)'}}>开始时间: </span>
                                            <span>{formatTs(estimateData.startTime)}</span>
                                        </div>
                                        <div>
                                            <span style={{color: 'var(--pt-text-dim)'}}>结束时间: </span>
                                            <span>{formatTs(estimateData.endTime)}</span>
                                        </div>
                                    </div>
                                </Card>
                                <Card size="small" style={{
                                    marginBottom: 12,
                                    background: 'var(--pt-bg-card)',
                                    borderColor: 'var(--pt-border-subtle)'
                                }}>
                                    <div style={{color: 'var(--pt-text-dim)', fontSize: 12, marginBottom: 8}}>数据明细
                                    </div>
                                    <Table
                                        dataSource={estimateData.tfDetails || []}
                                        rowKey="tf"
                                        size="small"
                                        pagination={false}
                                        columns={[
                                            {title: '周期', dataIndex: 'tf', key: 'tf', width: 60},
                                            {
                                                title: '预热范围',
                                                key: 'warmup',
                                                width: 260,
                                                render: (_: any, r: any) => `${formatTs(r.warmupStart)} ~ ${formatTs(r.warmupEnd)}`
                                            },
                                            {
                                                title: '预热',
                                                key: 'warmupCount',
                                                width: 50,
                                                align: 'right' as const,
                                                render: (_: any, r: any) => r.warmupCount + '根'
                                            },
                                            {
                                                title: '回测',
                                                key: 'backtestCount',
                                                width: 50,
                                                align: 'right' as const,
                                                render: (_: any, r: any) => r.backtestCount + '根'
                                            },
                                            {
                                                title: '总计',
                                                key: 'totalCount',
                                                width: 50,
                                                align: 'right' as const,
                                                render: (_: any, r: any) => r.totalCount + '根'
                                            },
                                        ]}
                                    />
                                </Card>
                                <Card size="small" style={{
                                    marginBottom: 12,
                                    background: 'var(--pt-bg-card)',
                                    borderColor: 'var(--pt-border-subtle)'
                                }}>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '12px 24px',
                                        fontSize: 14
                                    }}>
                                        <div>
                                            <span style={{color: 'var(--pt-text-dim)'}}>预估决策次数: </span>
                                            <strong style={{
                                                color: 'var(--pt-primary)',
                                                fontFamily: 'IBM Plex Mono, monospace',
                                                fontSize: 20
                                            }}>
                                                {estimateData.estimatedDecisions}
                                            </strong>
                                            <span style={{color: 'var(--pt-text-dim)'}}> 次</span>
                                        </div>
                                        <div>
                                            <span style={{color: 'var(--pt-text-dim)'}}>预估耗时: </span>
                                            <strong style={{
                                                color: 'var(--pt-bull)',
                                                fontFamily: 'IBM Plex Mono, monospace',
                                                fontSize: 20
                                            }}>
                                                约 {estimateData.estimatedMinutes}
                                            </strong>
                                            <span style={{color: 'var(--pt-text-dim)'}}> 分钟</span>
                                        </div>
                                    </div>
                                    <div style={{marginTop: 8, color: 'var(--pt-text-dim)', fontSize: 11}}>
                                        * 按每次AI决策约5秒估算，实际受模型响应速度影响
                                    </div>
                                </Card>
                            </>
                        )}
                    </div>
                )}
            </Modal>

            <Modal
                title="从Freqtrade导入历史数据"
                open={importModalOpen}
                onCancel={() => setImportModalOpen(false)}
                onOk={handleImport}
                okText="开始导入"
                confirmLoading={importing}
                width={500}
                className="dark-modal-confirm"
            >
                <div style={{color: 'var(--pt-text-secondary)', marginBottom: 12}}>
                    数据目录: /tmp/main-docker/data
                </div>
                {importFiles.length === 0 ? (
                    <div style={{color: 'var(--pt-text-dim)', textAlign: 'center', padding: 20}}>
                        未发现可导入文件（*.json.gz）
                    </div>
                ) : (
                    importFiles.map((f, i) => (
                        <div key={i} style={{
                            padding: '8px 0', borderBottom: '1px solid var(--pt-border-subtle)',
                            color: 'var(--pt-text-primary)', fontSize: 13
                        }}>
                            <Checkbox defaultChecked style={{marginRight: 8}}/>
                            {f.exchange}/{f.pair}-{f.timeframe}.json.gz
                            <span style={{color: 'var(--pt-text-dim)', marginLeft: 8}}>
                                {(f.fileSize / 1024).toFixed(1)} KB
                            </span>
                        </div>
                    ))
                )}
            </Modal>
        </div>
    );
};

function formatTs(ts: number) {
    if (!ts) return '-';
    return dayjs(ts).format('YYYY-MM-DD HH:mm');
}

export default AiBacktestListPage;
